import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { embedDashboard, UiConfigType, EmbeddedDashboard,} from '@preset-sdk/embedded';
import { GetDashboardsByTagRequest, GuestTokenRequest, GuestTokenResponse } from "../../interfaces/Bi-Dashboard/bi-dashboard.interface";
import { environment } from "environment";
import { firstValueFrom, map } from "rxjs";
import { DisplayValue } from "../../components/labour-hours/labour-hours.component";
import { StorageService } from "../storage.service";
import { embedDashboardConstants } from "../../constants/bi-dashboard.constants";

@Injectable({ providedIn: "root" })
export class BiToolService {
  private _httpClient = inject(HttpClient);
  private _storageService = inject(StorageService);
  private readonly supersetDomain = environment.biDashboardSupersetDomain;

  async embedDashboard(
    dashboardId: number,
    containerElement: HTMLElement,
    uiConfig?: UiConfigType
  ): Promise<EmbeddedDashboard | void> {
    if (!containerElement) {
      console.error(embedDashboardConstants.mountPointNotFound);
      return;
    }

    try {
      const uuid = await firstValueFrom(this.getDashboardUUID(dashboardId));
      const tokenRequest: GuestTokenRequest = {
        dashboardId: dashboardId,
        corporationId: this._storageService.getSelectedCorporationId()!,
        facilityId: this._storageService.getSelectedFacilityId()!,
      };
      const embeddedDashboard = await embedDashboard({
        id: uuid,
        supersetDomain: this.supersetDomain,
        mountPoint: containerElement,
        fetchGuestToken: () => {
          return this.getGuestToken(tokenRequest);
        },
        dashboardUiConfig: uiConfig ?? {
          hideTitle: false,
          hideChartControls: false,
          filters: {
            expanded: false,
          },
        },
        iframeTitle: embedDashboardConstants.iframeTitle,
        referrerPolicy:
          embedDashboardConstants.referrerPolicy as ReferrerPolicy,
      });

      return embeddedDashboard;
    } catch (error) {
      console.error(embedDashboardConstants.embedFailed, error);
    }
  }

  syncPresetDashboards(){
    const url = environment.endpoint + `/BiDashboard/sync-preset-dashboards`;
    return this._httpClient.get(url);
  }

  getCategories() {
    const url = environment.endpoint + `/BiDashboard/get-categories`;
    return this._httpClient.get<DisplayValue<number>[]>(url);
  }

  getDashboards(category: string) {
    const body: GetDashboardsByTagRequest = {
      tag: category,
      facilityId: this._storageService.getSelectedFacilityId()!,
    };
    const url = environment.endpoint + `/BiDashboard/get-dashboards`;
    return this._httpClient.post<DisplayValue<number>[]>(url, body);
  }

  getGuestToken(request: GuestTokenRequest): Promise<string> {
    const url = environment.endpoint + `/BiDashboard/guest-token`;
    return firstValueFrom(
      this._httpClient
        .post<GuestTokenResponse>(url, request)
        .pipe(map((response) => response.token))
    );
  }

  getDashboardUUID(dashboardId: number) {
    const url = environment.endpoint + `/BiDashboard/get-uuid/${dashboardId}`;
    return this._httpClient.get(url, { responseType: "text" });
  }
}